import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  convertToParamMap,
} from '@angular/router';
import { filter, map } from 'rxjs';
import { Store } from '@ngrx/store';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { TranslatePipe } from '@ngx-translate/core';

import { UiActions, selectSidebarCollapsed } from '@stores/ui';
import { ConsoleContextService } from '@core/services/console-context.service';
import { IconComponent, IconName } from '@shared/icons/icon/icon.component';
import { ShellStateService } from '../shell/shell-state.service';
import { TenantSelectorComponent } from './tenant-selector/tenant-selector.component';
import { SubscriptionSelectorComponent } from './subscription-selector/subscription-selector.component';
import { collectionNavigationQuery } from '@features/console/pages/monitoring/shared/monitoring-navigation';

interface NavItem {
  labelKey: string;
  icon: IconName;
  path: string;
  monitoringSection?: 'requests' | 'analytics' | 'errors';
}

const PLATFORM_NAV: NavItem[] = [
  { labelKey: 'SIDEBAR.NAV.TENANTS', icon: 'building', path: 'tenants' },
  { labelKey: 'SIDEBAR.NAV.ACCOUNTS_MANAGEMENT', icon: 'users', path: 'accounts' },
  { labelKey: 'SIDEBAR.NAV.GROUPS', icon: 'layers', path: 'groups' },
];

const CONSOLE_NAV: NavItem[] = [
  { labelKey: 'NAV.DASHBOARD', icon: 'dashboard', path: 'dashboard' },
  { labelKey: 'NAV.AGENTS', icon: 'bot', path: 'agents' },
  { labelKey: 'NAV.API_KEYS', icon: 'key', path: 'api-keys' },
  {
    labelKey: 'NAV.MONITORING',
    icon: 'activity',
    path: 'monitoring/requests',
    monitoringSection: 'requests',
  },
  {
    labelKey: 'MONITORING.TABS.ANALYTICS',
    icon: 'chart',
    path: 'monitoring/analytics',
    monitoringSection: 'analytics',
  },
  {
    labelKey: 'MONITORING.ERRORS.NAV_TITLE',
    icon: 'alert',
    path: 'monitoring/errors',
    monitoringSection: 'errors',
  },
];

@Component({
  selector: 'mtm-app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    IconComponent,
    TenantSelectorComponent,
    SubscriptionSelectorComponent,
    TranslatePipe,
  ],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSidebarComponent {
  private readonly store = inject(Store);
  private readonly bridge = inject(MtmAuthBridgeService);
  private readonly router = inject(Router);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  readonly context = inject(ConsoleContextService);
  readonly shell = inject(ShellStateService);

  readonly platformNav = PLATFORM_NAV;
  readonly consoleNav = CONSOLE_NAV;

  private readonly collapsedState = this.store.selectSignal(selectSidebarCollapsed);
  // The mobile drawer always shows labels, whatever the desktop collapse state.
  readonly collapsed = computed(() => this.collapsedState() && !this.shell.mobileNavOpen());

  // Base segments for AI console links, sourced from the selected context
  readonly consoleBase = computed<string[] | null>(() => {
    const tenant = this.context.selectedTenant();
    const sub = this.context.selectedSubscription();
    return tenant && sub ? ['/console', tenant.tenantUuid, sub.subscriptionUuid] : null;
  });

  onToggleSidebar(): void {
    if (this.shell.mobileNavOpen()) {
      this.shell.mobileNavOpen.set(false);
      return;
    }
    this.store.dispatch(UiActions.toggleSidebar());
  }

  /** The sidebar's console context never becomes a Monitoring data filter. */
  private readonly monitoringLocation = computed(() => {
    const base = this.consoleBase();
    if (!base) return null;
    const prefix = `${base.join('/')}/monitoring`;
    const path = this.currentUrl().split(/[?#]/)[0];
    if (path !== prefix && !path.startsWith(`${prefix}/`)) return null;
    const segments = path.slice(prefix.length).split('/').filter(Boolean);
    const fromEntity =
      (segments[0] === 'requests' || segments[0] === 'conversations') && segments.length > 1;
    const fromErrors =
      fromEntity && this.router.parseUrl(this.currentUrl()).queryParams['listView'] === 'errors';
    return {
      section:
        segments[0] === 'analytics'
          ? 'analytics'
          : segments[0] === 'errors' || fromErrors
            ? 'errors'
            : 'requests',
      fromEntity,
    };
  });

  sectionQuery(item: NavItem) {
    const query = { ...this.monitoringQuery() };
    if (item.monitoringSection !== 'errors') delete query['errorType'];
    return query;
  }

  readonly monitoringQuery = computed(() => {
    const location = this.monitoringLocation();
    if (!location) return {};
    const params = this.router.parseUrl(this.currentUrl()).queryParams;
    return {
      ...params,
      ...collectionNavigationQuery(convertToParamMap(params), location.fromEntity),
    };
  });

  isConsoleActive(item: NavItem): boolean {
    if (item.monitoringSection) {
      return this.monitoringLocation()?.section === item.monitoringSection;
    }
    const path = this.currentUrl().split(/[?#]/)[0];
    const target = `${this.consoleBase()?.join('/')}/${item.path}`;
    return path === target || path.startsWith(`${target}/`);
  }

  /** Collapsed: the context card is icons only, so a click on it expands the sidebar. */
  expand(): void {
    if (this.collapsed()) this.store.dispatch(UiActions.setSidebarCollapsed({ collapsed: false }));
  }

  onLogout(): void {
    this.bridge.logout$().subscribe();
  }
}
